/**
 * `Onboarding` shell — ADR-0049 (skills-first 4-step IA, mandatory
 * verify, gated Complete). Four steps:
 *
 *   0 — Add site                  (AddSiteStep)
 *   1 — Skills · detect & match   (SkillsStep — NEW)
 *   2 — Install + verify          (InstallVerifyStep — reshape of the
 *                                  prior InstallAndSkillsStep, with
 *                                  ProofStatusPanel.onVerified wired to
 *                                  machine.next(); no Continue button)
 *   3 — Complete                  (CompleteStep — NEW; verify-gated
 *                                  "You're live" hero + condensed
 *                                  ProtocolStackSection, or a
 *                                  NOT-YET-VERIFIED safety-net face
 *                                  when status isn't *_active)
 *
 * The prior `VerifyStep` + `InstallAndSkillsStep` + `OtherPlatformPanel`
 * have been removed; their wire contracts (POST /v1/sites,
 * POST /v1/sites/:id/detect-platform, OAuth init endpoints,
 * skills.saveForSite, profile_extraction.start/getLatest, proof-token /
 * proof-check, interest "Notify me", enableSampleTraffic) are ALL
 * preserved verbatim — only the composition changes.
 */

import { ArrowRight, ChevronLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { Site } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useSites } from '../../app/providers.js';
import type { View } from '../../app/view-router.js';
import { isClaimEntry } from '../../lib/prefill-domain.js';
import { useC } from '../../lib/theme.js';
import { createOnboardingMachine } from '../../lib/onboarding-machine.js';
import type {
  OnboardingMachine,
  OnboardingStep,
} from '../../lib/onboarding-machine.js';
import {
  shouldShowWelcome,
  wasWelcomed,
} from '../../lib/onboarding-preferences.js';
import { Tooltip } from '../../components/atoms/index.js';
import { AddSiteStep } from './AddSiteStep.js';
import {
  deriveName,
  peekPrefillDomain,
  stripPrefillDomainParam,
} from './DomainStep.js';
import { CompleteStep } from './CompleteStep.js';
import { InstallVerifyStep } from './InstallVerifyStep.js';
import { SkillsStep } from './SkillsStep.js';
import { WelcomeStep } from './WelcomeStep.js';

const sansFont = "'Geist', sans-serif";

/**
 * Resume rules per ADR-0049 §"Resume mapping" with the founder's
 * correction (pending_proof → Skills, NOT Install+verify):
 *
 *   - `pending_proof` → step 1 (Skills). A freshly-created site is
 *     pending_proof; the create flow sets it active, which fires the
 *     resume effect. It MUST land on Skills — the same place
 *     `machine.next()` goes from Add. The prior mapping (pending_proof
 *     → Install+verify) re-introduced the founder's "added a site,
 *     jumped past Skills" defect because the resume effect would
 *     override the forward `next()` to Skills, skipping it entirely.
 *     A returning unverified merchant who needs to re-verify ALSO
 *     starts at Skills and clicks forward; that's fine — Continue
 *     advances to Install+verify on step 2.
 *
 *   - `discovery_active` / `transactional_active` → step 3 (Complete).
 *     They're live; show the success hero. Skills edits for live
 *     merchants happen on the dashboard's Skills view, not in
 *     onboarding (per ADR-0049's "why not pending_proof / live
 *     resume Skills" rationale).
 *
 *   - Anything else (null / undefined / suspended / a future status)
 *     → step 0 (Add site).
 */
export function resumeStepFor(site: Site | null | undefined): OnboardingStep {
  if (site?.status === 'pending_proof') return 1;
  if (
    site?.status === 'discovery_active'
    || site?.status === 'transactional_active'
  ) {
    return 3;
  }
  return 0;
}

export interface OnboardingProps {
  setView: (v: View) => void;
}

export function Onboarding({ setView }: OnboardingProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const { activeSite, sites, addSite, setActiveSite } = useSites();

  const initialStep = resumeStepFor(activeSite);

  const [step, setStep] = useState<OnboardingStep>(initialStep);
  // ADR-0167 §1 — the one-screen welcome renders before step 0, only on
  // the new-account entry path (`/onboarding` pathname from the auth
  // verify redirect, no OAuth-return or navigation params, browser-local
  // `welcomed` flag unset). The URL/flag part is frozen at mount (a
  // mid-flow `?view=` write must not dismiss the welcome out from under
  // the user); the no-sites condition is checked at render, so a
  // resuming merchant whose sites land AFTER mount (or who mounts with
  // a site already in context) falls straight through to the machine —
  // an account with any site has outgrown the welcome regardless of
  // browser-local flags.
  const [welcomeEntry, setWelcomeEntry] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? shouldShowWelcome(
          {
            pathname: window.location.pathname,
            search: window.location.search,
          },
          wasWelcomed(),
        )
      : false,
  );
  const showWelcome =
    welcomeEntry && activeSite === null && sites.length === 0;
  const machineRef = useRef<OnboardingMachine | null>(null);
  const trackedSiteIdRef = useRef<string | undefined>(activeSite?.site_id);
  // ADR-0186 S1 — auto-create from the marketing carry-over. When the
  // verify redirect lands here with ?prefill_domain= and the account has
  // no sites, the shell creates the site itself (the same authenticated
  // POST /v1/sites DomainStep issues, caps and validation intact) and
  // the site-switch effect below lands on Skills. The founder-hated
  // "Add domain AGAIN" screen never renders on this path; on failure the
  // prefill is left in the URL so DomainStep renders PREFILLED as the
  // one-click fallback.
  const autoTriedRef = useRef(false);
  const [autoCreating, setAutoCreating] = useState(false);
  const [autoDomain, setAutoDomain] = useState('');

  if (!machineRef.current) {
    machineRef.current = createOnboardingMachine({
      initialStep,
      onChange: (next) => setStep(next),
    });
  }

  const machine = machineRef.current;

  useEffect(() => {
    if (autoTriedRef.current) return;
    // ADR-0201 amendment (qa #570 round 2) — claim entries NEVER
    // auto-create: a publicly shareable link with no email round-trip
    // must end at the DomainStep's visible one-click offer instead.
    if (isClaimEntry()) return;
    const domain = peekPrefillDomain();
    if (domain === '' || activeSite !== null || sites.length > 0) return;
    autoTriedRef.current = true;
    setAutoDomain(domain);
    setAutoCreating(true);
    // The prefill path skips the welcome face — the account was created
    // one click ago; another interstitial is exactly the redundancy
    // ADR-0186 exists to kill.
    setWelcomeEntry(false);
    // Land on the site's resume step in the SAME batch that clears the
    // setting-up face. Leaving it to the site-switch effect below moves
    // the step one render late, so the first frame after the face cleared
    // still showed step 0 (the Add/Domain step): a one-frame flash of
    // the exact re-ask this path exists to hide.
    const adopt = (site: Site): void => {
      stripPrefillDomainParam();
      machine.goTo(resumeStepFor(site));
      setActiveSite(site);
    };
    void (async () => {
      try {
        const created = await api.sites.create({ domain, name: deriveName(domain) });
        addSite(created);
        adopt(created);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'conflict') {
          // The site already exists on this account (existing-account
          // sign-in carrying a pending domain) — resume it.
          try {
            const list = await api.sites.list();
            const existing = list.find(
              (site) => site.domain.toLowerCase() === domain,
            );
            if (existing !== undefined) {
              adopt(existing);
              return;
            }
          } catch {
            /* fall through to the prefilled DomainStep */
          }
        }
        // Any other failure (plan limit, network, 5xx): leave the
        // prefill in the URL — DomainStep consumes it and renders the
        // one-click "Set up {domain}" fallback. Never a blank re-ask.
      } finally {
        setAutoCreating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSite, sites, api, addSite, setActiveSite]);

  // ADR-0186 declutter (2026-07-14 founder report) — the redundant
  // "What domain should Sill govern?" re-ask. It renders when the
  // account already HAS the carried-over site but no `activeSite` is
  // selected (the site list resolved after mount, or a prior
  // signup-confirm created it, so auto-create above bails on
  // `sites.length > 0` and `resumeStepFor(null)` falls to the Add step).
  // On the prefill carry-over path only, adopt the matching (or any
  // pending_proof) site as active so the flow resumes at Skills — the
  // domain stays editable there via the DomainChip. Gated on a present
  // prefill so it never hijacks a deliberate "add another site".
  useEffect(() => {
    if (activeSite !== null) return;
    const prefill = peekPrefillDomain();
    if (prefill === '' || sites.length === 0) return;
    // Claim entries adopt an EXACT domain match only — falling back to
    // "any pending site" would hijack the claim toward an unrelated
    // domain (qa #570 round 2). The post-verify carry keeps the
    // pending_proof fallback (its redundant-re-ask rationale).
    const match =
      sites.find((s) => s.domain.toLowerCase() === prefill)
      ?? (isClaimEntry()
        ? undefined
        : sites.find((s) => s.status === 'pending_proof'));
    // No match on a claim entry: fall through with the prefill intact —
    // DomainStep consumes it into the visible offer (resumeStepFor(null)
    // mounts it, via the claim effect below when a site was pre-selected).
    if (match !== undefined) {
      stripPrefillDomainParam();
      setActiveSite(match);
    }
  }, [activeSite, sites, setActiveSite]);

  // ADR-0201 amendment (qa #570 round-2 major 2) — the signed-in claim
  // branch. SiteProvider auto-selects an activeSite on bootstrap, so
  // both effects above bail and the carried domain would be silently
  // dropped (and the stale param would later hijack an unrelated
  // "add a site"). Consume it here: an exact match becomes the active
  // site (claim-your-own-site → that site's real state); no match
  // clears the selection so resumeStepFor(null) mounts DomainStep,
  // which renders the prefilled VISIBLE OFFER (claim entries never
  // auto-create).
  useEffect(() => {
    if (!isClaimEntry()) return;
    if (activeSite === null) return;
    const prefill = peekPrefillDomain();
    if (prefill === '') return;
    const match = sites.find((s) => s.domain.toLowerCase() === prefill);
    if (match !== undefined) {
      stripPrefillDomainParam();
      if (match.site_id !== activeSite.site_id) setActiveSite(match);
      return;
    }
    // Leave the param — DomainStep consumes it into the offer form.
    setActiveSite(null);
  }, [activeSite, sites, setActiveSite]);

  // When the user switches site via the TopBar mid-onboarding, jump
  // the flow to that site's correct resume step. Without this, an
  // unverified site shown after verifying a different one inherits
  // the previous site's step and looks "already verified". Preserved
  // verbatim from the prior shell.
  useEffect(() => {
    const currentId = activeSite?.site_id;
    if (currentId === trackedSiteIdRef.current) return;
    trackedSiteIdRef.current = currentId;
    machine.goTo(resumeStepFor(activeSite));
  }, [activeSite, machine]);

  // ADR-0186 S1 — the brief setting-up face while auto-create runs.
  // Prevents a DomainStep flash between the /welcome click and Skills.
  if (autoCreating) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-24 text-center">
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 12,
          }}
        >
          SETTING UP
        </div>
        <h1
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: 'clamp(32px, 6vw, 48px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
            marginBottom: 16,
          }}
        >
          {autoDomain}
        </h1>
        <p style={{ fontFamily: sansFont, fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
          Creating your site. One moment.
        </p>
      </div>
    );
  }

  // ADR-0167 §1 — welcome face. No "Sites" back-link: the merchant has
  // no sites yet and the only forward path is the flow itself.
  if (showWelcome) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <WelcomeStep onContinue={() => setWelcomeEntry(false)} />
      </div>
    );
  }

  return (
    <div className={`max-w-3xl mx-auto px-4 sm:px-8 ${step === 3 ? 'pt-8 pb-10 sm:pt-12 sm:pb-16' : 'py-10 sm:py-16'}`}>
      {/* Step 2 (Install) Back goes to Skills — never to /welcome or a
          domain re-ask (founder 2026-07-16). Steps 0/1 keep the Sites nav.
          Finish-later sits on the same row (justify-between) on step 2
          (2026-10-21), with the "setup guide keeps your place" detail in a
          Tooltip (NOT title=). */}
      {/* Step 3 (complete) has no back link (founder 2026-10-29 round 2):
          Open dashboard is the way forward and the nav reaches Sites. */}
      {step !== 3 ? (
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={() => (step === 2 ? machine.back() : setView('sites'))}
          className="flex items-center gap-2"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
        >
          <ChevronLeft size={13} /> {step === 2 ? 'Back' : 'Sites'}
        </button>
        {step === 2 ? (
          <Tooltip content="The setup guide keeps your place.">
            <button
              type="button"
              onClick={() => setView('sites')}
              className="inline-flex items-center gap-1"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = C.text; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = C.muted; }}
              data-testid="install-verify-finish-later"
            >
              Finish later <ArrowRight size={12} />
            </button>
          </Tooltip>
        ) : null}
      </div>
      ) : null}
      {step === 0 && <AddSiteStep onNext={() => machine.next()} setView={setView} />}
      {step === 1 && <SkillsStep onNext={() => machine.next()} />}
      {step === 2 && (
        <InstallVerifyStep
          onNext={() => machine.next()}
          onWatchDemo={() => setView('simulation')}
        />
      )}
      {step === 3 && (
        <CompleteStep setView={setView} onBack={() => machine.back()} />
      )}
    </div>
  );
}
